// Methodology-audit runner. Walks a guideline's items, runs locators per item,
// emits ItemEvidence + PatternFindings.

import type { ChecklistGuideline, ChecklistItem } from '../checklists/types';
import type { ManuscriptDocument } from '../pdf/types';
import { findLocatorMatches } from './locator-engine';
import { methodologyFindingId } from './finding-id';
import type { ItemEvidence, MethodologyRun, PatternFinding } from './types';

export function runMethodology(
  guideline: ChecklistGuideline,
  doc: ManuscriptDocument,
): { run: MethodologyRun; findings: PatternFinding[] } {
  const items: ItemEvidence[] = [];
  const findings: PatternFinding[] = [];

  for (const section of guideline.sections) {
    for (const item of section.items) {
      const matches = collectMatches(item, doc);
      const status: ItemEvidence['status'] = matches.length > 0 ? 'located' : 'not_located';
      const evidence: ItemEvidence = {
        itemId: item.id,
        itemText: item.text,
        status,
        severityIfMissing: item.severity_if_missing,
        matches: matches.slice(0, 3),
        sectionHint: item.section_hint,
      };
      items.push(evidence);

      if (status === 'not_located') {
        findings.push({
          id: methodologyFindingId(item.id, 'not_located'),
          category: 'methodology',
          severity: item.severity_if_missing,
          checklistItemId: item.id,
          findingText: `${item.id} not located by pattern engine: ${truncate(item.text, 120)}`,
          evidencePassage: '',
          evidenceOffset: -1,
          section: item.section_hint ?? 'unknown',
          layer: 'pattern',
        });
      } else {
        // Located. Surface as an info-level finding only when severity_if_missing
        // is red, so the editor sees confirmation that critical items have evidence.
        if (item.severity_if_missing === 'red') {
          const best = matches[0];
          findings.push({
            id: methodologyFindingId(item.id, 'located'),
            category: 'methodology',
            severity: 'info',
            checklistItemId: item.id,
            findingText: `${item.id} located: ${truncate(item.text, 100)}`,
            evidencePassage: best.text,
            evidenceOffset: best.offset,
            section: best.section,
            layer: 'pattern',
          });
        }
      }
    }
  }

  const locatedCount = items.filter((i) => i.status === 'located').length;
  const locatedFraction = items.length === 0 ? 0 : locatedCount / items.length;

  return {
    run: {
      guidelineId: guideline.guideline_id,
      guidelineVersion: guideline.version,
      items,
      locatedFraction,
    },
    findings,
  };
}

function collectMatches(item: ChecklistItem, doc: ManuscriptDocument) {
  const all: ReturnType<typeof findLocatorMatches> = [];
  const seen = new Set<string>();
  for (const loc of item.locators) {
    for (const m of findLocatorMatches(loc, doc)) {
      const key = `${m.section}:${m.offset}`;
      if (seen.has(key)) continue;
      seen.add(key);
      all.push(m);
    }
  }
  return all;
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}
